import {DrawerFooter} from "@/widget/DrawerModal/DrawerModal";
import TextField from "@mui/material/TextField";
import {Controller, useForm} from "react-hook-form";
import {FormLabel, MenuItem, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import React from "react"
import Box from "@mui/material/Box";
import {useAllDict} from "@/store/dict.c";
import {useDrawerModal, useGlobalActions} from "@/store/global.c";
import * as z from "zod";
import {zodResolver} from "@hookform/resolvers/zod";
import {emptyValue} from "@/helper/method";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {useCreateContact, useQueryContact, useUpdateContact} from "@/service/contact.s";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CustomerPO} from "@/model/customer";
import {WriteChanceForm} from "@/pages/Chance/WriteChance";
import {isNumber} from "lodash";

const validationSchema = z.object({
    name: z.string({message: '请填写姓名'}).nonempty({message: "请填写姓名"}),
    mobile: z.string({message: "请填写联系电话"}).nonempty({message: "请填写联系电话"}),
    email: z.ostring(),
    firsted: z.number({message: "请选择首要联系人"}).default(1),
    gender: z.number().nullish(),
    post: z.string().nullable(),
    customerName: z.unknown(),
    customerId: z.number({message: "请选择客户名称"}),
    id: z.number().default(0),
    // email: z.string().email("请填写正确的邮箱格式").max(60, "邮箱长度不能超过60位").refine((val) => val.length > 0)
});

export type WriteContactForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export const WriteContact = () => {
    const {boole, gender} = useAllDict()
    const {id: drawerId, write} = useDrawerModal()
    const {setToast} = useGlobalActions()
    const {mutateAsync: createContactMutate, isPending: createContactPending} = useCreateContact()
    const {mutateAsync: updateContactMutate, isPending: updateContactPending} = useUpdateContact()
    const {data: contactData} = useQueryContact({id: drawerId!}, isNumber(drawerId))
    const contact = contactData?.data
    const {onSearch} = useDrawerModal()
    const {setDrawerModal} = useGlobalActions()

    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteContactForm>({
        values: contact,
        resolver: zodResolver(validationSchema),
        defaultValues,
    });

    const onSubmit = async (values: WriteContactForm) => {
        const result = write == "add" ? await createContactMutate(values) : await updateContactMutate({...values, id: drawerId!})
        if (result.code == 0) {
            onSearch && onSearch()
            setDrawerModal({open: false})
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return (
        <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
            <Grid spacing={2} container>
                <Grid size={12}>
                    <SearchData<WriteChanceForm, CustomerPO> errMsg={errors?.customerId?.message} control={control} attr={"customerName"} attrId={"customerId"} setValue={setValue}
                                                             label={"客户名称"}/>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>姓名</FormLabel>
                        <TextField
                            error={!!errors?.name}
                            helperText={errors?.name?.message}
                            {...register('name')}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 10},
                                    endAdornment: (
                                        <PermIdentityOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写姓名"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true} required={true}>
                        <FormLabel>联系电话</FormLabel>
                        <TextField
                            error={!!errors?.mobile}
                            helperText={errors?.mobile?.message}
                            {...register(`mobile`)}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 15},
                                    endAdornment: (
                                        <PhoneOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写联系电话"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>首要联系人</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择首要联系人"} select={true}>
                                {boole?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`firsted`}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel>性别</FormLabel>
                        <Controller control={control} render={({field: {onChange, value}}) => {
                            return <TextField value={value || null} onChange={event => onChange(event.target.value)}
                                              placeholder={"选择性别"} select={true}>
                                {gender?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        }} name={`gender`}/>
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="email">联系邮箱</FormLabel>
                        <TextField
                            id="email"
                            {...register(`email`)}
                            error={!!errors?.email}
                            helperText={errors?.email?.message}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 60},
                                    endAdornment: (
                                        <AlternateEmailOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写联系邮箱"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
                <Grid size={6}>
                    <FormControl fullWidth={true}>
                        <FormLabel htmlFor="post">职位</FormLabel>
                        <TextField
                            id="post"
                            {...register(`post`)}
                            slotProps={{
                                input: {
                                    inputProps: {maxLength: 20},
                                    endAdornment: (
                                        <WorkOutlineOutlinedIcon color="disabled"/>
                                    ),
                                },
                            }}
                            placeholder="填写职位"
                            variant="outlined"
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <DrawerFooter loading={createContactPending || updateContactPending} onReset={() => reset(defaultValues)}/>
        </Box>
    );
};
